import React, { useState } from 'react';
import Icon from '../AppIcon';
import Button from '../ui/Button';

const QuoteRequestForm = ({
    requestData,
    onSuccess,
    onError,
    confirmButtonText = 'Envoyer la Demande',
    className = ''
}) => {
    const [isProcessing, setIsProcessing] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);

    const handleSubmit = async (event) => {
        event?.preventDefault();

        setIsProcessing(true);
        setErrorMessage('');

        try {
            // Simulate a brief delay for UX
            await new Promise(resolve => setTimeout(resolve, 500));

            setShowSuccessAnimation(true);

            setTimeout(() => {
                setShowSuccessAnimation(false);
                onSuccess?.({
                    quoteNumber: requestData?.quoteNumber,
                    requestData
                });
            }, 2500);

        } catch (error) {
            console.error('Quote request processing error:', error);
            setErrorMessage('Une erreur inattendue s\'est produite. Veuillez réessayer.');
            onError?.(error);
            setIsProcessing(false);
        }
    };

    return (
        <div className={`relative bg-card rounded-lg border border-border p-6 ${className}`}>
            {!showSuccessAnimation ? (
                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="space-y-4">
                        <div className="bg-muted/50 rounded-lg p-4">
                            <h3 className="text-lg font-semibold text-foreground mb-3">Récapitulatif de votre demande</h3>

                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">Type de service :</span>
                                    <span className="font-medium text-foreground">{requestData?.serviceType}</span>
                                </div>

                                {requestData?.itemName && (
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">Article :</span>
                                        <span className="font-medium text-foreground">{requestData?.itemName}</span>
                                    </div>
                                )}

                                {requestData?.duration && (
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">Durée :</span>
                                        <span className="font-medium text-foreground">{requestData?.duration}</span>
                                    </div>
                                )}

                                <div className="border-t border-border pt-2 mt-2">
                                    <div className="flex justify-between items-center">
                                        <span className="text-muted-foreground">Montant estimé :</span>
                                        <span className="text-xl font-bold text-primary">{requestData?.totalAmount}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <div className="flex items-start gap-3">
                                <Icon name="Info" size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
                                <div className="text-sm text-blue-800">
                                    <p className="font-medium mb-1">Demande de devis gratuite</p>
                                    <p>
                                        En soumettant cette demande, notre équipe vous contactera dans les plus brefs délais
                                        pour finaliser les détails et vous proposer un devis personnalisé.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {errorMessage && (
                        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
                            <div className="flex items-start gap-3">
                                <Icon name="AlertCircle" size={20} className="text-destructive flex-shrink-0 mt-0.5" />
                                <p className="text-destructive text-sm">{errorMessage}</p>
                            </div>
                        </div>
                    )}

                    <Button
                        type="submit"
                        disabled={isProcessing}
                        className="w-full"
                        size="lg"
                        iconName={isProcessing ? "Loader2" : "Send"}
                        iconClassName={isProcessing ? "animate-spin" : ""}
                        iconPosition="left"
                    >
                        {isProcessing ? 'Envoi en cours...' : confirmButtonText}
                    </Button>

                    <div className="flex items-center justify-center space-x-2 text-xs text-muted-foreground">
                        <Icon name="Shield" size={14} />
                        <span>Vos informations sont sécurisées et confidentielles</span>
                    </div>
                </form>
            ) : (
                <div className="absolute inset-0 bg-white/95 backdrop-blur-sm rounded-lg flex items-center justify-center z-50">
                    <div className="text-center space-y-6 p-8">
                        <div className="relative">
                            <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center animate-pulse">
                                <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center animate-bounce">
                                    <Icon name="Check" size={32} className="text-white" />
                                </div>
                            </div>
                            <div className="absolute inset-0 w-20 h-20 mx-auto border-4 border-green-300 rounded-full animate-ping opacity-30"></div>
                            <div className="absolute inset-0 w-20 h-20 mx-auto border-2 border-green-400 rounded-full animate-ping opacity-50" style={{ animationDelay: '0.5s' }}></div>
                        </div>

                        <div className="space-y-2">
                            <h3 className="text-2xl font-bold text-green-600">
                                Demande Envoyée! 🎉
                            </h3>
                            <p className="text-green-700 font-medium">
                                Nous avons bien reçu votre demande
                            </p>
                            {requestData?.quoteNumber && (
                                <p className="text-sm text-green-600">
                                    Référence : {requestData?.quoteNumber}
                                </p>
                            )}
                        </div>

                        <div className="bg-green-50 rounded-lg p-4">
                            <p className="text-sm text-green-800">
                                Notre équipe vous contactera dans les plus brefs délais pour finaliser votre demande.
                            </p>
                        </div>

                        <div className="flex justify-center space-x-1">
                            <div className="w-2 h-2 bg-green-500 rounded-full animate-bounce"></div>
                            <div className="w-2 h-2 bg-green-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                            <div className="w-2 h-2 bg-green-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default QuoteRequestForm;
